import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { CuiIconComponent } from '@idealink-material/ui-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { APP_ROUTES } from '@/app.router.config';
import { LayoutService } from '@/layout/service/layout.service';

interface SidebarNavItem {
  id: string;
  label: string;
  icon: string;
  routerLink?: string;
  children?: SidebarNavItem[];
}

interface SidebarNavGroup {
  label: string;
  items: SidebarNavItem[];
}

/**
 * Vertical icon+label nav rail rendered beside the routed content. Logo and
 * grouped section labels live here (not in `app-topnav`), matching the
 * platform-admin design reference (design/platform-dashboard-tenants.html).
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CuiIconComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <aside
      class="flex flex-col bg-white border-r border-[var(--border-default)] h-full transition-[width] duration-200 py-5"
      [class.w-64]="!collapsed()"
      [class.w-[72px]]="collapsed()"
      [class.px-4]="!collapsed()"
      [class.px-2]="collapsed()"
    >
      <div class="font-extrabold text-lg px-2.5 pb-7 whitespace-nowrap overflow-hidden" [class.text-center]="collapsed()">
        @if (!collapsed()) {
          <span class="text-[#172033]">TENANT<span class="text-[var(--dash-primary)]">OS</span></span>
        } @else {
          <span class="text-[var(--dash-primary)]">OS</span>
        }
      </div>

      @for (group of groups; track group.label) {
        @if (!collapsed()) {
          <div class="text-[10px] font-extrabold text-[#98a2b3] uppercase tracking-wider px-2.5 pb-2">
            {{ group.label | translate }}
          </div>
        }
        <ul class="flex flex-col gap-1 mb-5">
          @for (item of group.items; track item.id) {
            <li>
              <button
                type="button"
                class="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors cursor-pointer"
                [class.justify-center]="collapsed()"
                [class.font-bold]="isActive(item)"
                [class.font-semibold]="!isActive(item)"
                [class.bg-[#eff4ff]]="isActive(item)"
                [class.text-[var(--dash-primary)]]="isActive(item)"
                [class.text-[#475467]]="!isActive(item)"
                [title]="collapsed() ? (item.label | translate) : null"
                (click)="select(item)"
              >
                <cui-icon [name]="item.icon" size="sm" />
                @if (!collapsed()) {
                  <span class="truncate">{{ item.label | translate }}</span>
                }
              </button>
            </li>
          }
        </ul>
      }
    </aside>
  `
})
export class AppSidebar {
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly collapsed = computed(() => this.layoutService.layoutState().staticMenuDesktopInactive);

  readonly groups: SidebarNavGroup[] = [
    {
      label: 'menuGroup.platform',
      items: [
        { id: '01', label: 'menuItem.dashboard', icon: 'dashboard', routerLink: `/${APP_ROUTES.DASHBOARD}` },
        { id: '02', label: 'menuItem.tenant', icon: 'tenancy', routerLink: `/${APP_ROUTES.TENANT}` },
        { id: '03', label: 'menuItem.plateFormUser', icon: 'group', routerLink: `/${APP_ROUTES.PLATE_FROM_USER}` },
      ],
    },
    {
      label: 'menuGroup.administration',
      items: [
        { id: '11', label: 'menuItem.setting', icon: 'settings', routerLink: `/${APP_ROUTES.SETTING}` },
        { id: '12', label: 'menuItem.auditLog', icon: 'fact_check', routerLink: `/${APP_ROUTES.AUDIT}` },
      ],
    },
  ];

  private readonly activeUrl = computed(() => this.currentUrl());

  isActive(item: SidebarNavItem): boolean {
    const url = this.activeUrl();
    if (item.routerLink && url.startsWith(item.routerLink)) {
      return true;
    }
    return !!item.children?.some((child) => !!child.routerLink && url.startsWith(child.routerLink));
  }

  select(item: SidebarNavItem): void {
    if (item.routerLink) {
      this.router.navigateByUrl(item.routerLink);
    }
  }
}
